// AuthProvider.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { app, db } from './firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [claims, setClaims] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const auth = getAuth(app);

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (u) => {
            try {
                // If any old anonymous sessions linger, sign them out immediately
                if (u?.isAnonymous) {
                    await signOut(auth);
                    setUser(null);
                    setClaims(null);
                    setIsAdmin(false);
                    setLoading(false);
                    return;
                }

                if (!u) {
                    setUser(null);
                    setClaims(null);
                    setIsAdmin(false);
                    setLoading(false);
                    return;
                }

                // We have a real user; load claims and admin status
                const tokenResult = await u.getIdTokenResult(true); // refresh to get latest custom claims
                const hasClaim = !!tokenResult?.claims?.admin;

                let inAdminsCollection = false;
                if (!hasClaim) {
                    const snap = await getDoc(doc(db, 'admins', u.uid));
                    inAdminsCollection = snap.exists();
                }

                setUser(u);
                setClaims(tokenResult?.claims ?? {});
                setIsAdmin(hasClaim || inAdminsCollection);
                setLoading(false);
            } catch (err) {
                console.error('Auth state handling failed:', err);
                setUser(null);
                setClaims(null);
                setIsAdmin(false);
                setLoading(false);
            }
        });

        return unsub;
    }, [auth]);

    return (
        <AuthContext.Provider value={{ user, claims, isAdmin, loading }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
